import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../Store';

const selectNutritionOnboarding = (state: RootState) => state.nutritionOnboarding;

// Current category selector
export const selectCurrentCategory = createSelector(
  [selectNutritionOnboarding],
  (state) => {
    const { activeCategoryIds, currentCategoryIndex, questionTypes } = state;
    const currentCategoryId = activeCategoryIds[currentCategoryIndex];
    return questionTypes.find(category => category.id === currentCategoryId) || null;
  }
);

// Questions of current category selector
export const selectCurrentCategoryQuestions = createSelector(
  [selectNutritionOnboarding],
  (state) => {
    const { activeCategoryIds, currentCategoryIndex, questionsByCategory } = state;
    const currentCategoryId = activeCategoryIds[currentCategoryIndex];
    return questionsByCategory[currentCategoryId] || [];
  }
);

// Question by id selector
export const selectQuestionById = createSelector(
  [selectNutritionOnboarding, (state: RootState, questionId: number) => questionId],
  (state, questionId) => {
    const { questionsByCategory } = state;
    for (const categoryQuestions of Object.values(questionsByCategory)) {
      const question = (categoryQuestions as any[]).find(q => q.id === questionId);
      if (question) return question;
    }
    return null;
  }
);

// Answers of current category selector
export const selectCurrentCategoryAnswers = createSelector(
  [selectCurrentCategoryQuestions],
  (questions) => {
    return questions.reduce((answers, question) => {
      answers[question.id] = question.customer_answer || [];
      return answers;
    }, {} as Record<number, any[]>);
  }
);

// Form validity selector for current category
export const selectIsCurrentFormValid = createSelector(
  [selectCurrentCategoryQuestions, selectNutritionOnboarding],
  (questions, state) => {
    // Import validation function
    const { validateQuestion } = require('@/utils/validation/questionValidation');
    
    // Check if there are any validation errors
    const hasValidationErrors = questions.some(question => 
      state.validationErrors[question.id]
    );
    
    if (hasValidationErrors) {
      return false;
    }

    // Validate each question using the proper validation logic
    return questions.every(question => {
      const result = validateQuestion(question);
      return result.isValid;
    });
  }
);

// Navigation state selectors
export const selectIsFirstCategory = createSelector(
  [selectNutritionOnboarding],
  (state) => state.currentCategoryIndex === 0
);

export const selectIsLastCategory = createSelector(
  [selectNutritionOnboarding],
  (state) => state.currentCategoryIndex === state.activeCategoryIds.length - 1
);

// Loading and error selectors
export const selectQuestionsLoading = createSelector(
  [selectNutritionOnboarding],
  (state) => state.questionsLoading
);

export const selectSavingAnswers = createSelector(
  [selectNutritionOnboarding],
  (state) => state.savingAnswers
);

export const selectSavingProgress = createSelector(
  [selectNutritionOnboarding],
  (state) => state.savingProgress
);

// All categories and questions for review screen
export const selectAllCategoriesWithAnswers = createSelector(
  [selectNutritionOnboarding],
  (state) => {
    const { questionTypes, questionsByCategory } = state;
    return questionTypes.map(category => ({
      ...category,
      questions: questionsByCategory[category.id] || []
    }));
  }
);

// Validation error selectors
export const selectValidationErrors = createSelector(
  [selectNutritionOnboarding],
  (state) => state.validationErrors
);

export const selectQuestionValidationError = createSelector(
  [selectNutritionOnboarding, (state: RootState, questionId: number) => questionId],
  (state, questionId) => state.validationErrors[questionId] || null
);